import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import TabbedDetailCard from '#/modules/commonComponents/layout/TabbedDetailCard';
import SalesOrderAllocationsReadonlySection
  from '#/modules/crmMgmt/salesOrder/components/SalesOrderAllocationsReadonlySection';
import SalesOrderLinesReadonlySection
  from '#/modules/crmMgmt/salesOrder/components/SalesOrderLinesReadonlySection';
import SalesOrderSnSelectionSection
  from '#/modules/crmMgmt/salesOrder/components/SalesOrderSnSelectionSection';
import {
  canAllocateSalesOrderInventory,
  canEditSalesOrderDraft,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';

/** 與 CommandBar「配置庫存」同一入口（tab 內第二入口，RWD 用）。 */
function renderAllocateAction(salesOrderId, enabled) {
  if (enabled && salesOrderId) {
    return (
      <Button asChild size="sm">
        <Link to={ROUTES.salesOrderAllocate(salesOrderId)}>配置庫存</Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" disabled>
      <span className="inline-flex items-center gap-0.5">
        配置庫存
        <ActionHintIcon tip="僅草稿／備貨中可配置庫存" ariaLabel="配置庫存說明" />
      </span>
    </Button>
  );
}

/** 選擇序號獨立頁入口（僅 DRAFT）。 */
function renderSnSelectAction(salesOrderId, enabled) {
  if (enabled && salesOrderId) {
    return (
      <Button asChild size="sm">
        <Link to={ROUTES.salesOrderSnSelect(salesOrderId)}>選擇序號</Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" disabled>
      <span className="inline-flex items-center gap-0.5">
        選擇序號
        <ActionHintIcon tip="僅草稿可選擇序號" ariaLabel="選擇序號說明" />
      </span>
    </Button>
  );
}

/**
 * 銷貨單詳情 Tabs：明細摘要／使用庫存／選擇序號（TabbedDetailCard，對齊客訂 DetailTabs）。
 */
const SalesOrderDetailTabs = ({ detail }) => {
  const salesOrderId = detail?.id ? String(detail.id) : '';
  const status = detail?.status;

  return (
    <TabbedDetailCard defaultValue="linesSummary" className="2xl:col-span-2">
      <TabbedDetailCard.List>
        <TabbedDetailCard.Trigger value="linesSummary">明細摘要</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="allocations">使用庫存</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="snSelections">選擇序號</TabbedDetailCard.Trigger>
      </TabbedDetailCard.List>

      <TabbedDetailCard.Pane value="linesSummary" title="明細摘要">
        <div className="px-4 py-4">
          <p className="mb-3 text-xs text-muted-foreground">
            銷貨產品、其他產品與數量一覽。
          </p>
          <SalesOrderLinesReadonlySection
            lines={detail?.lines ?? []}
            manualLines={detail?.manualLines ?? []}
            embedded
          />
        </div>
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="allocations"
        title="使用庫存"
        actions={renderAllocateAction(salesOrderId, canAllocateSalesOrderInventory(status))}
      >
        <SalesOrderAllocationsReadonlySection
          lines={detail?.lines ?? []}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="snSelections"
        title="選擇序號"
        actions={renderSnSelectAction(salesOrderId, canEditSalesOrderDraft(status))}
      >
        <SalesOrderSnSelectionSection detail={detail} />
      </TabbedDetailCard.Pane>
    </TabbedDetailCard>
  );
};

SalesOrderDetailTabs.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
    manualLines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default SalesOrderDetailTabs;
